/**
 * @fileoverview Carry the page someone was sent to through sign-in.
 *
 * An email link — "Add notes" on a missed-notes alert — opens a page deep in
 * the app, and on a phone the browser it opens in is often signed out. Without
 * this the instructor signs in and lands on the dashboard, and the one tap the
 * email promised becomes a hunt for the class and the day.
 *
 * sessionStorage, for the same reason `pendingInvite` uses it: the Google
 * round trip leaves Hans and comes back with only OAuth's own parameters.
 *
 * Only a path on this site is ever kept or returned: a stored value is read
 * back into `navigate`, and anything that could name another origin
 * ("//evil.example", "/\\evil.example") would make sign-in an open redirect.
 * Every storage access is wrapped, so a browser with storage disabled still
 * signs in and simply lands in the usual place.
 */

const KEY = "hans_return_to";

/** A path on this site worth returning to: not the landing page or sign-in itself. */
export function isSafeReturn(path) {
  if (typeof path !== "string" || !path.startsWith("/")) return false;
  if (path.startsWith("//") || path.startsWith("/\\")) return false;
  const bare = path.split(/[?#]/)[0];
  return bare !== "/" && bare !== "/login" && bare !== "/onboarding";
}

/** Remember where they were going, when it is somewhere worth going back to. */
export function stashReturnTo(path) {
  if (!isSafeReturn(path)) return;
  try {
    sessionStorage.setItem(KEY, path);
  } catch {
    /* Storage disabled: they land on the dashboard instead. */
  }
}

/** Read it back and clear it — it is for exactly one landing. */
export function consumeReturnTo() {
  try {
    const path = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return isSafeReturn(path) ? path : null;
  } catch {
    return null;
  }
}
